import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import MobileActionSheet from './MobileActionSheet';
import messages from './AvailabilityToggle.messages';
import './AvailabilityToggle.scss';

const { block, element } = bem('availability-toggle');

const AvailabilityToggle = ({ enabled, onChangeEnabled, isReadOnly, testId }) => {
  const intl = useIntl();
  const isMobile = useSelector(selectIsMobile);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  if (isMobile) {
    const options = [
      {
        label: (
          <>
            <span className={element('dot', { available: true })} />
            {intl.formatMessage(messages.availableLabel)}
          </>
        ),
        onClick: () => onChangeEnabled(true),
      },
      {
        label: (
          <>
            <span className={element('dot', { unavailable: true })} />
            {intl.formatMessage(messages.unavailableLabel)}
          </>
        ),
        onClick: () => onChangeEnabled(false),
      },
    ];

    return (
      <>
        <button
          type="button"
          className={block({ disabled: isReadOnly })}
          data-testid={testId}
          onClick={() => setIsSheetOpen(true)}
          disabled={isReadOnly}>
          <span className={element('dot', { available: enabled, unavailable: !enabled })} />
          <span>
            {enabled ? intl.formatMessage(messages.availableLabel) : intl.formatMessage(messages.unavailableLabel)}
          </span>
          {!isReadOnly && (
            <span className={element('caret')}>
              <SvgArrow direction="down" />
            </span>
          )}
        </button>
        <MobileActionSheet isOpen={isSheetOpen} onClose={() => setIsSheetOpen(false)} options={options} />
      </>
    );
  }

  return (
    <Dropdown attachment="right" disabled={isReadOnly}>
      {({ toggleMenu }) => (
        <>
          <DropdownToggle className={block()} isRoundRadiusStyle onClick={isReadOnly ? undefined : toggleMenu}>
            <span className={element('dot', { available: enabled, unavailable: !enabled })} />
            {enabled ? <Message message={messages.availableLabel} /> : <Message message={messages.unavailableLabel} />}
          </DropdownToggle>
          {!isReadOnly && (
            <DropdownMenu>
              <DropdownItem isSelected={enabled} onClick={() => onChangeEnabled(true)}>
                <span className={element('dot', { available: true })} />
                <Message message={messages.availableLabel} />
              </DropdownItem>
              <DropdownItem isSelected={!enabled} onClick={() => onChangeEnabled(false)}>
                <span className={element('dot', { unavailable: true })} />
                <Message message={messages.unavailableLabel} />
              </DropdownItem>
            </DropdownMenu>
          )}
        </>
      )}
    </Dropdown>
  );
};

AvailabilityToggle.propTypes = {
  enabled: PropTypes.bool.isRequired,
  onChangeEnabled: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  testId: PropTypes.string,
};

export default AvailabilityToggle;
